import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';

const severityDots: Record<string, string> = {
  red: 'bg-red-500',
  orange: 'bg-amber-500',
  yellow: 'bg-yellow-500',
};

const severityText: Record<string, string> = {
  red: 'text-red-400',
  orange: 'text-amber-400',
  yellow: 'text-yellow-400',
};

const severityBorder: Record<string, string> = {
  red: 'border-l-[3px] border-red-500',
  orange: 'border-l-[3px] border-amber-500',
  yellow: 'border-l-[3px] border-yellow-500',
};

interface NeedsAttentionProps {
  /** When provided, skip internal fetch and use these items directly */
  items?: any[];
  /** Compact strip mode for Zone 1 (hides "All clear" empty state) */
  compact?: boolean;
}

const NeedsAttention: React.FC<NeedsAttentionProps> = ({ items: externalItems, compact }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [internalItems, setInternalItems] = useState<any[]>([]);

  // Only fetch internally when no external items provided (backward compat)
  useEffect(() => {
    if (externalItems !== undefined) return;
    api.get('/analytics/cross-system/attention-items')
      .then(r => setInternalItems(r.data.items || []))
      .catch(console.error);
  }, [externalItems]);

  const items = externalItems ?? internalItems;

  // Empty state
  if (items.length === 0) {
    if (compact) return null; // In compact mode, render nothing when empty
    return (
      <div className={`p-4 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        All clear — nothing needs attention
      </div>
    );
  }

  // Compact strip mode: single row summary for Zone 1
  if (compact) {
    const bySeverity = { red: 0, orange: 0, yellow: 0 };
    items.forEach(item => {
      if (bySeverity[item.severity as keyof typeof bySeverity] !== undefined) {
        bySeverity[item.severity as keyof typeof bySeverity]++;
      }
    });

    return (
      <div className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs ${
        darkMode ? 'bg-red-900/10 border border-red-800/30' : 'bg-red-50 border border-red-200'
      }`}>
        {bySeverity.red > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-red-400 font-medium">{bySeverity.red} critical</span>
          </span>
        )}
        {bySeverity.orange > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-amber-400 font-medium">{bySeverity.orange} warning</span>
          </span>
        )}
        {bySeverity.yellow > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-500" />
            <span className="text-yellow-400 font-medium">{bySeverity.yellow} info</span>
          </span>
        )}
        <button
          onClick={() => navigate('/work/chat')}
          className="ml-auto text-purple-400 hover:text-purple-300 font-medium"
        >
          Fix Now &rarr;
        </button>
      </div>
    );
  }

  // Full rendering mode (original behavior)
  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-3px)}75%{transform:translateX(3px)}}`}</style>
      {items.map((item, i) => (
        <div
          key={i}
          onClick={() => item.action_url && navigate(item.action_url)}
          className={`p-3 rounded-lg cursor-pointer transition-all duration-200 hover:translate-x-1 ${severityBorder[item.severity] || ''} ${
            item.severity === 'red' ? 'animate-[shake_0.5s_ease-in-out]' : ''
          } ${
            darkMode ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-white hover:bg-gray-50'
          }`}
          style={item.severity === 'orange' ? { boxShadow: '0 0 8px rgba(245,158,11,0.15)' } : undefined}
        >
          <div className="flex items-start gap-2">
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${severityDots[item.severity] || 'bg-gray-500'}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${severityText[item.severity] || 'text-gray-400'}`}>{item.title}</p>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{item.context}</p>
              {item.action_label && (
                <p className="text-xs mt-1 text-purple-400">{item.action_label} &rarr;</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NeedsAttention;
